import React, { useState, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const getNestedValue = (obj, key) => {
  return key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : ''), obj);
};

const DataTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  onDuplicate,
  searchable = true,
  paginated = true,
  pageSize = 50,
  filters = [],
  actions = true,
}) => {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    let result = [...data];

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          const val = getNestedValue(row, col.key);
          return val !== null && val !== undefined && String(val).toLowerCase().includes(q);
        })
      );
    }

    // Filters
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value) {
        result = result.filter((row) => String(getNestedValue(row, key)) === value);
      }
    });

    // Sort
    if (sortKey) {
      result.sort((a, b) => {
        const aVal = getNestedValue(a, sortKey);
        const bVal = getNestedValue(b, sortKey);
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return sortDir === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    return result;
  }, [data, search, sortKey, sortDir, activeFilters, columns]);

  const totalPages = paginated ? Math.ceil(filtered.length / pageSize) : 1;
  const paged = paginated ? filtered.slice(page * pageSize, (page + 1) * pageSize) : filtered;

  // Reset page when filters change
  const updateFilter = (key, value) => {
    setActiveFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const renderCell = (row, col, rowIndex) => {
    const val = getNestedValue(row, col.key);
    switch (col.type) {
      case 'badge':
        return (
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
            dark ? 'bg-[#FF9800]/15 text-[#FF9800]' : 'bg-indigo-100 text-indigo-700'
          }`}>
            {val}
          </span>
        );
      case 'thumbnail':
        return val ? (
          <img src={val} alt="" className="w-8 h-8 rounded object-cover" />
        ) : (
          <div className={`w-8 h-8 rounded ${dark ? 'bg-[#333]' : 'bg-gray-200'}`} />
        );
      case 'number':
        return <span className="tabular-nums">{val}</span>;
      default:
        return <span>{val}</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {searchable && (
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className={`px-3 py-2 rounded-lg border text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              dark ? 'bg-[#1A1A1A] border-[#333] text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        )}
        {filters.map((f) => (
          <select
            key={f.key}
            value={activeFilters[f.key] || ''}
            onChange={(e) => updateFilter(f.key, e.target.value)}
            className={`px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              dark ? 'bg-[#1A1A1A] border-[#333] text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="">{f.label}: All</option>
            {f.options.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        ))}
        <span className={`ml-auto text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
          {filtered.length} item{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className={`overflow-x-auto rounded-lg border ${dark ? 'border-[#333]' : 'border-gray-200'}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={dark ? 'bg-[#1A1A1A]' : 'bg-gray-50'}>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={col.sortable !== false ? () => handleSort(col.key) : undefined}
                  className={`px-4 py-3 text-left font-medium ${
                    col.sortable !== false ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-[#333]' : ''
                  } ${dark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key && (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path d={sortDir === 'asc' ? 'M5.293 9.707l4.293-4.293a1 1 0 011.414 0l4.293 4.293a1 1 0 01-.707 1.707H6a1 1 0 01-.707-1.707z' : 'M14.707 10.293l-4.293 4.293a1 1 0 01-1.414 0l-4.293-4.293A1 1 0 015.414 9H14.586a1 1 0 01.707 1.707z'} />
                      </svg>
                    )}
                  </span>
                </th>
              ))}
              {actions && <th className={`px-4 py-3 text-right font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className={`px-4 py-8 text-center ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                  No items found
                </td>
              </tr>
            ) : (
              paged.map((row, i) => {
                const globalIndex = data.indexOf(row);
                return (
                  <tr key={globalIndex} className={`border-t ${dark ? 'border-[#333] hover:bg-[#1A1A1A]' : 'border-gray-100 hover:bg-gray-50'} transition-colors`}>
                    {columns.map((col) => (
                      <td key={col.key} className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {renderCell(row, col, i)}
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {onEdit && (
                            <button onClick={() => onEdit(row, globalIndex)} title="Edit"
                              className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-[#333] ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          {onDuplicate && (
                            <button onClick={() => onDuplicate(row, globalIndex)} title="Duplicate"
                              className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-[#333] ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          )}
                          {onDelete && (
                            <button onClick={() => onDelete(row, globalIndex)} title="Delete"
                              className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginated && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className={`px-3 py-1.5 rounded text-sm ${
              page === 0 ? 'opacity-40 cursor-not-allowed' : ''
            } ${dark ? 'text-gray-400 hover:bg-[#333]' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Previous
          </button>
          <span className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className={`px-3 py-1.5 rounded text-sm ${
              page >= totalPages - 1 ? 'opacity-40 cursor-not-allowed' : ''
            } ${dark ? 'text-gray-400 hover:bg-[#333]' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTable;
